import { createContext, ReactNode, useContext } from 'react';

import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type DialogMessageTone = 'warn' | 'danger' | 'error';

const ICONS: Record<DialogMessageTone | 'lock', ReactNode> = {
  warn: (
    <>
      <path d="M12 4 2.8 19.5h18.4z" />
      <path d="M12 10v4.5M12 17.3v.2" />
    </>
  ),
  danger: (
    <>
      <path d="M4.5 7h15M9.5 7V5.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V7" />
      <path d="m6.5 7 .9 11.2a1.5 1.5 0 0 0 1.5 1.3h6.2a1.5 1.5 0 0 0 1.5-1.3L17.5 7M10.2 10.5V16M13.8 10.5V16" />
    </>
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v4.5M12 15.8v.2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
};

export const DialogMessage = ({
  tone = 'warn',
  icon,
  children,
}: {
  tone?: DialogMessageTone;
  // Defaults to the tone's icon (warning triangle, trash, error circle).
  icon?: DialogMessageTone | 'lock';
  children: ReactNode;
}) => {
  const theme = useTheme();
  const warn = tone === 'warn';
  return (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <Box
        aria-hidden
        sx={{
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          width: 36,
          height: 36,
          borderRadius: '10px',
          background: warn
            ? theme.custom?.messageWarnBg
            : theme.custom?.messageDangerBg,
          color: warn
            ? theme.custom?.messageWarnIcon
            : theme.custom?.messageDangerIcon,
          '& svg': {
            width: 20,
            height: 20,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.8,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          },
        }}
      >
        <svg viewBox="0 0 24 24">{ICONS[icon ?? tone]}</svg>
      </Box>
      <Box
        sx={{
          margin: 0,
          fontSize: '14px',
          lineHeight: '21px',
          color: theme.custom?.stepText,
          '& b': { fontWeight: 600, color: theme.custom?.brandNavy },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const smallDialogSx = {
  '& .MuiDialog-paper': { width: '420px', maxWidth: 'calc(100% - 24px)' },
};
export const smallDialogBodySx = { padding: '18px 22px 8px !important' };

// Pages on the new design (the Employee Center) wrap themselves in this so
// the shared confirm / warning hooks use the new dialog; other pages keep
// the old one.
const RedesignDialogsContext = createContext(false);
export const RedesignDialogsProvider = ({
  children,
}: {
  children: ReactNode;
}) => (
  <RedesignDialogsContext.Provider value>
    {children}
  </RedesignDialogsContext.Provider>
);
export const useRedesignDialogs = () => useContext(RedesignDialogsContext);

// Footer buttons for the new confirm dialog. `danger` makes the confirm red.
export const ConfirmDialogButtons = ({
  cancelLabel,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}: {
  // Omit for a single-button message (OK).
  cancelLabel?: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel?: () => void;
  onConfirm: () => void;
}) => {
  const theme = useTheme();
  return (
    <>
      {cancelLabel && (
        <Button variant="outlined" onClick={onCancel}>
          {cancelLabel}
        </Button>
      )}
      <Button
        variant="contained"
        onClick={onConfirm}
        sx={
          danger
            ? {
                background: `${theme.custom?.dangerButtonBg} !important`,
                borderColor: `${theme.custom?.dangerButtonBg} !important`,
                color: `${theme.background.white} !important`,
                '&:hover': {
                  background: `${theme.custom?.dangerButtonHoverBg} !important`,
                },
              }
            : undefined
        }
      >
        {confirmLabel}
      </Button>
    </>
  );
};
